import { useState, useEffect } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Input, Button,
} from '@heroui/react';
import { useCreateContact, useUpdateContact } from '@/api/companies';
import type { Contact } from '@estimator/shared';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  contact?: Contact;
}

export default function ContactForm({ isOpen, onClose, companyId, contact }: ContactFormProps) {
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const isEdit = !!contact;

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (contact) {
      setName(contact.name);
      setTitle(contact.title ?? '');
      setPhone(contact.phone ?? '');
      setEmail(contact.email ?? '');
    } else {
      setName(''); setTitle(''); setPhone(''); setEmail('');
    }
  }, [contact, isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      title: title || undefined,
      phone: phone || undefined,
      email: email || undefined,
    };
    if (isEdit) {
      await updateContact.mutateAsync({ companyId, contactId: contact!.id, ...payload });
    } else {
      await createContact.mutateAsync({ companyId, ...payload });
    }
    onClose();
  };

  const isPending = createContact.isPending || updateContact.isPending;
  const inputClassNames = {
    inputWrapper: 'border border-[var(--border)] bg-[var(--bg-panel)]',
    input: 'text-[var(--text-primary)]',
    label: 'text-[var(--text-secondary)]',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Contact' : 'Add Contact'}</ModalHeader>
        <ModalBody className="flex flex-col gap-3">
          <Input label="Name" value={name} onValueChange={setName} isRequired variant="bordered" classNames={inputClassNames} />
          <Input label="Title" placeholder="e.g. Project Manager" value={title} onValueChange={setTitle} variant="bordered" classNames={inputClassNames} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Phone" value={phone} onValueChange={setPhone} variant="bordered" classNames={inputClassNames} />
            <Input label="Email" value={email} onValueChange={setEmail} variant="bordered" classNames={inputClassNames} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>Cancel</Button>
          <Button onPress={handleSubmit} isLoading={isPending} isDisabled={!name.trim()}>
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
